import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type { PageResponse, RepertoireEntry } from '@/lib/types';

const REPERTOIRE_FETCH_SIZE = 200;

/**
 * Pure selector — given a flat list of `RepertoireEntry` rows and a target
 * pieceId, return a `Map<arrangementId, userArrangementId>` for the matching
 * subset. Used by `useUserArrangementsForPiece` and any test that wants to
 * stub the query result without going through React Query.
 */
export function selectUserArrangementsForPiece(
  entries: readonly RepertoireEntry[],
  pieceId: string,
): Map<string, string> {
  const map = new Map<string, string>();
  for (const entry of entries) {
    if (entry.pieceId === pieceId) {
      map.set(entry.arrangementId, entry.userArrangementId);
    }
  }
  return map;
}

export type UserArrangementsForPieceResult = {
  isLoading: boolean;
  isError: boolean;
  data: Map<string, string>;
};

/**
 * Returns a Map of `arrangementId → userArrangementId` for the user's
 * existing repertoire entries on the requested piece. Reads a single page of
 * `GET /repertoire?size=200`. Until a backend `pieceId` filter lands (see
 * ROADMAP), this window covers the typical user; rare entries beyond the
 * window are missed but the 409 contract on POST still catches the race.
 */
export function useUserArrangementsForPiece(
  pieceId: string | undefined,
): UserArrangementsForPieceResult {
  const query = useQuery<PageResponse<RepertoireEntry>>({
    queryKey: ['repertoire', { page: 0, size: REPERTOIRE_FETCH_SIZE }],
    queryFn: () =>
      api.fetch<PageResponse<RepertoireEntry>>(
        `/repertoire?page=0&size=${REPERTOIRE_FETCH_SIZE}`,
      ),
    enabled: Boolean(pieceId),
  });
  const entries = query.data?.content ?? [];
  return {
    isLoading: query.isLoading,
    isError: query.isError,
    data: pieceId ? selectUserArrangementsForPiece(entries, pieceId) : new Map(),
  };
}

export const REPERTOIRE_QUERY_KEY_FOR_FORM_PANE = ['repertoire', { page: 0, size: REPERTOIRE_FETCH_SIZE }] as const;
